import { useEnvironment, useExperiments } from '@wix/yoshi-flow-editor';
import React from 'react';
import { SpinnerTypes, Text } from 'wix-ui-tpa/cssVars';
import { SlotsStatus } from '../../../../../types/types';
import { SlotsPerDay } from '../../../../../utils/slotsPerDay/slotsPerDay';
import { NoAvailableSlotsViewModel } from '../../../ViewModel/noAvailableSlotsViewModel/noAvailableSlotsViewModel';
import { AvailabilityPlugin } from '../../AvailabilityPlugin/AvailabilityPlugin';
import NoAvailableSlots from '../../NoAvailableSlots/NoAvailableSlots';
import { WidgetLoader } from '../../WidgetLoader/WidgetLoader';
import { DayAvailability } from './DayAvailability/DayAvailability';
import { classes, st } from './WeekAvailability.st.css';
import { DataHooks } from './constants';

export type DayWithSlotsFactory<SlotsViewModel> = (props: {
  date: string;
  slotsViewModel: SlotsViewModel;
}) => React.ReactNode;

export type DayWithoutSlotsIndicationFactory<SlotsViewModel> = (props: {
  text: string;
  slotsViewModel: SlotsViewModel;
}) => React.ReactNode;

export interface WeekAvailabilityProps<SlotsViewModel> {
  slotsStatus: SlotsStatus;
  slotsPerDays: SlotsPerDay<SlotsViewModel>[];
  noAvailableSlotsViewModel?: NoAvailableSlotsViewModel;
  dayWithSlotsFactory: DayWithSlotsFactory<SlotsViewModel>;
  dayWithoutSlotsIndicationFactory: DayWithoutSlotsIndicationFactory<SlotsViewModel>;
  accessibleDays?: boolean;
  onKeyDown?: (e: React.KeyboardEvent<HTMLDivElement>) => void;
  filterDate?: (date: Date) => boolean;
}

const WeekAvailability = <SlotsViewModel extends object>({
  slotsPerDays,
  slotsStatus,
  noAvailableSlotsViewModel,
  dayWithSlotsFactory,
  dayWithoutSlotsIndicationFactory,
  accessibleDays,
  onKeyDown,
  filterDate,
}: WeekAvailabilityProps<SlotsViewModel>) => {
  const { isEditor } = useEnvironment();
  const { experiments } = useExperiments();

  const isDisableDatesAfterReschedulePlanExpirationEnabled = experiments.enabled('specs.bookings.DisableDatesAfterReschedulePlanExpiration');

  const NoAvailability = () => {
    return noAvailableSlotsViewModel &&
      slotsStatus === SlotsStatus.NO_AVAILABLE_SLOTS ? (
      <NoAvailableSlots viewModel={noAvailableSlotsViewModel!} />
    ) : null;
  };

  const Loader = () => {
    return slotsStatus === SlotsStatus.LOADING ? (
      <WidgetLoader spinnerType={SpinnerTypes.slim} />
    ) : null;
  };

  return (
    <div
      className={classes.root}
      data-hook={DataHooks.WeekAvailability}
      onKeyDown={onKeyDown}
    >
      <div className={classes.week}>
        {slotsPerDays?.map(
          (slotsPerDay: SlotsPerDay<SlotsViewModel>, index) => {
            const { date, slots, isToday, dayWithoutSlots, accessibility } =
              slotsPerDay;
            const isPastDate = !isEditor && slotsPerDay.isPastDate;
            const isDisabledByFilter =
              filterDate ? !filterDate(new Date(date)) : false;
            const isDisabled = isPastDate || isDisabledByFilter;

            return (
              <div
                className={st(classes.dayWrapper, {
                  isToday: !isEditor && isToday,
                  isDisabled,
                })}
                tabIndex={accessibleDays ? 0 : undefined}
                data-hook={`${DataHooks.DayAvailability}-${date}`}
                data-dayindex={index}
                role="group"
                aria-label={accessibility.dayDetailsAriaLabel}
                key={date}
                id={date}
              >
                <DayDetails slotsPerDay={slotsPerDay} />
                {slotsStatus !== SlotsStatus.LOADING && 
                  (!isDisableDatesAfterReschedulePlanExpirationEnabled || !isDisabled) && (
                    <DayAvailability
                      date={date}
                      accessibility={accessibility}
                      slots={
                        dayWithSlotsFactory({
                          date,
                          slotsViewModel: slots,
                        })
                      }
                      noSlots={
                        dayWithoutSlotsIndicationFactory({
                          slotsViewModel: slots,
                          text: dayWithoutSlots,
                        })
                      }
                    />
                  )
                }
              </div>
            );
          },
        )}
      </div>
      <NoAvailability />
      <Loader />
      <AvailabilityPlugin />
    </div>
  );
};

interface DayDetailsProps<SlotsViewModel> {
  slotsPerDay: SlotsPerDay<SlotsViewModel>;
}

const DayDetails = <SlotsViewModel extends object>({
  slotsPerDay,
}: DayDetailsProps<SlotsViewModel>) => {
  const { weekday, day } = slotsPerDay;

  return (
    <div
      aria-hidden="true"
      className={classes.dayDetails}
      data-hook={DataHooks.DayDetails}
    >
      <Text className={classes.weekday}>{weekday}</Text>
      <Text className={classes.day}>{day}</Text>
    </div>
  );
};

export default WeekAvailability;
